import { ThemeButtonSchema, themeColor, type ThemeButtonDto, type ThemeColorKey, type ThemeStylesDto } from '@xshop/shared';
import { buttonGeometry } from '../../themes/buttonTokens';

// Resolving `{ token: 'colors.primary' }` against the store's own tokens, which is the point of the whole
// engine: a heading bound to a token follows the shop when its palette is repainted, while a literal stays
// exactly where the merchant put it.
//
// The grammar is the path into `ThemeStylesDto` — `colors.primary`, `fonts.heading`, `radius.md`,
// `spacing.lg`, `buttons.primary.background`. That is the spelling `elements/catalog/styleTokens.ts` and
// `elements/presets/presetTokens.ts` already write and the one `STYLE_TOKENS` offers the inspector, so it
// is the spelling this file reads. A bare name is read as a colour, since that is the group a merchant
// reaches for most and the one the brief spells `{ token: 'primary' }`.
//
// A name outside the grammar resolves to null. Nothing downstream writes a declaration for it, so an
// unknown token costs its one property and the element keeps every other: never `undefined` in a
// stylesheet, never a throw on a shopper's page.

/**
 * The parts of one button's skin a token may name — read off `ThemeButtonSchema` rather than restated,
 * so a part added there is resolvable here without a second edit. It was restated once, and the copy
 * silently refused every geometry token the day the schema grew one.
 */
const BUTTON_PARTS: readonly string[] = ThemeButtonSchema.keyof().options;

/** The two buttons a theme paints; a token names one of them rather than carrying its own hexes. */
const BUTTON_VARIANTS = ['primary', 'secondary'] as const;

/** The three fonts that answer a whole stack rather than the bare family name the theme stores. */
const FONT_FAMILIES = ['heading', 'body', 'accent'] as const;

type ButtonVariant = (typeof BUTTON_VARIANTS)[number];

// Wraps one of the theme's families in a stack, so a face the shopper has not got still lands on something
// readable rather than on the browser's serif of last resort.
function fontStack(family: string): string {
  return `'${family.replace(/['"\\]/g, '')}', system-ui, -apple-system, 'Segoe UI', sans-serif`;
}

// Derives one button from the palette, for a theme whose styles block paints no button of its own. Mirrors
// `paletteButtons` in the souk theme, which is private to it.
function paletteButton(theme: ThemeStylesDto, variant: ButtonVariant): ThemeButtonDto {
  const { colors, radius } = theme;
  const size = buttonGeometry(theme.fonts, theme.spacing);
  if (variant === 'secondary') {
    return {
      background: colors.text, gradientFrom: colors.dark, textColor: colors.onDark,
      hoverBackground: colors.dark, borderColor: colors.text, radius: radius.sm, ...size
    };
  }
  return {
    background: colors.primary, gradientFrom: colors.accent, textColor: colors.onPrimary,
    hoverBackground: colors.primaryHover, borderColor: colors.primary, radius: radius.md, ...size
  };
}

// Reads one key off a group of tokens, answering null rather than undefined for a name it has not got.
// A group may now hold a nested scale beside its flat entries, and a scale is not a value — reading one
// as if it were would print `[object Object]` into a stylesheet.
function pickToken(group: Readonly<Record<string, unknown>>, key: string): string | null {
  const value = group[key];
  if (typeof value === 'number') return String(value);
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Reads one of the store's fonts. The three families answer a stack; `baseSize` and `headingWeight`
 * answer themselves; and a dotted rest — `sizes.lg`, `weights.bold`, `lineHeights.tight` — answers off
 * the optional scale of that name, which a theme written before the scales existed simply has not got.
 */
function fontToken(theme: ThemeStylesDto, key: string): string | null {
  const [group, step] = key.split('.');
  if (step) {
    const scale = (theme.fonts as Readonly<Record<string, unknown>>)[group];
    return scale && typeof scale === 'object' ? pickToken(scale as Record<string, unknown>, step) : null;
  }
  const family = (FONT_FAMILIES as readonly string[]).includes(key) ? pickToken(theme.fonts, key) : null;
  if (family) return fontStack(family);
  return key === 'baseSize' || key === 'headingWeight' ? pickToken(theme.fonts, key) : null;
}

// Reads a `buttons.<variant>.<part>` token off the theme's buttons, or off the palette when it paints none.
function buttonToken(theme: ThemeStylesDto, variant: string, part: string): string | null {
  if (!(BUTTON_VARIANTS as readonly string[]).includes(variant)) return null;
  if (!(BUTTON_PARTS as readonly string[]).includes(part)) return null;
  const named = variant as ButtonVariant;
  return pickToken(theme.buttons?.[named] ?? paletteButton(theme, named), part);
}

// Reads one colour, an optional shop colour the theme left out answered by its palette fallback.
const colorToken = (theme: ThemeStylesDto, key: string): string | null => themeColor(theme.colors, key as ThemeColorKey) || null;

// Reads a `<group>.<key>` token off one of the flat scales a theme ships.
function scaleToken(theme: ThemeStylesDto, group: string, key: string): string | null {
  if (group === 'colors') return colorToken(theme, key);
  if (group === 'radius') return pickToken(theme.radius, key);
  if (group === 'spacing') return pickToken(theme.spacing, key);
  // The depth scale is optional the way the font scales are: a theme naming no shadows resolves none,
  // the declaration is dropped, and the element stays exactly as flat as it was before the scale existed.
  if (group === 'shadows') return pickToken(theme.shadows ?? {}, key);
  return group === 'fonts' ? fontToken(theme, key) : null;
}

// Resolves one token name against the store's tokens, answering null for a name the vocabulary does not hold.
export function resolveStyleToken(name: string, theme: ThemeStylesDto): string | null {
  const parts = name.split('.');
  if (parts.length === 1) return colorToken(theme, parts[0]);
  if (parts.length === 2) return scaleToken(theme, parts[0], parts[1]);
  return parts.length === 3 && parts[0] === 'buttons' ? buttonToken(theme, parts[1], parts[2]) : null;
}
